import React, { useRef, useEffect, useState } from 'react';
import './CausalityVisualizer.css';

interface CausalEvent {
  id: string;
  year: number;
  category: string;
  description: string;
  significance: number;
  tags: string[];
  causedBy?: string;
  consequences?: string[];
}

interface CausalityChain {
  chainId: string;
  triggerEvent: CausalEvent;
  consequences: CausalEvent[];
  probability: number;
  completed: boolean;
}

interface CausalityVisualizerProps {
  chains: CausalityChain[];
  allEvents: CausalEvent[];
  onEventClick: (event: CausalEvent) => void;
}

export const CausalityVisualizer: React.FC<CausalityVisualizerProps> = ({
  chains,
  allEvents,
  onEventClick,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [selectedChain, setSelectedChain] = useState<string | null>(null);
  const [hoveredEvent, setHoveredEvent] = useState<CausalEvent | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    drawCausalityGraph(ctx, rect.width, rect.height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chains, selectedChain, allEvents]);

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      battle: '#ff6b6b',
      discovered: '#4ecdc4',
      founded: '#45b7d1',
      destroyed: '#e74c3c',
      cataclysm: '#9b59b6',
      cultural: '#f39c12',
      religious: '#3498db',
    };
    return colors[category] || '#95a5a6';
  };

  const drawCausalityGraph = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    ctx.clearRect(0, 0, width, height);

    const padding = { top: 60, bottom: 60, left: 80, right: 80 };
    const graphWidth = width - padding.left - padding.right;

    // Find year range
    const allYears = allEvents.map(e => e.year);
    const minYear = Math.min(...allYears);
    const maxYear = Math.max(...allYears);
    const yearRange = maxYear - minYear || 1;

    // Draw time axis
    ctx.strokeStyle = '#34495e';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding.left, height - padding.bottom);
    ctx.lineTo(width - padding.right, height - padding.bottom);
    ctx.stroke();

    // Draw year labels
    ctx.fillStyle = '#7f8c8d';
    ctx.font = '11px "Sometype Mono", monospace';
    ctx.textAlign = 'center';

    const yearStep = Math.max(100, Math.floor(yearRange / 8));
    for (let year = minYear; year <= maxYear; year += yearStep) {
      const x = padding.left + ((year - minYear) / yearRange) * graphWidth;
      ctx.fillText(year.toString(), x, height - padding.bottom + 20);
    }

    // Draw causality chains
    const displayChains = selectedChain 
      ? chains.filter(c => c.chainId === selectedChain)
      : chains.slice(0, 5); // Show first 5 chains if none selected

    displayChains.forEach((chain, chainIndex) => {
      const chainColor = `hsl(${chainIndex * 60}, 70%, 50%)`;
      
      // Draw trigger event
      const triggerX = padding.left + ((chain.triggerEvent.year - minYear) / yearRange) * graphWidth;
      const triggerY = padding.top + (chainIndex * 80) + 40;

      ctx.fillStyle = getCategoryColor(chain.triggerEvent.category);
      ctx.beginPath();
      ctx.arc(triggerX, triggerY, 12, 0, Math.PI * 2);
      ctx.fill();

      // Draw chain label
      ctx.fillStyle = chainColor;
      ctx.font = 'bold 12px "Sometype Mono", monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`Chain ${chainIndex + 1}`, padding.left - 10, triggerY + 4);

      // Draw consequences with connecting lines
      chain.consequences.forEach((consequence, consIndex) => {
        const consX = padding.left + ((consequence.year - minYear) / yearRange) * graphWidth;
        const consY = triggerY + 60 + (consIndex * 30);

        // Draw connecting line
        ctx.strokeStyle = chainColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(triggerX, triggerY + 12);
        
        // Curved line
        const midX = (triggerX + consX) / 2;
        const midY = (triggerY + consY) / 2;
        ctx.quadraticCurveTo(midX, triggerY + 30, consX, consY - 12);
        ctx.stroke();

        // Draw arrow head
        const angle = Math.atan2(consY - 12 - midY, consX - midX);
        ctx.beginPath();
        ctx.moveTo(consX, consY - 12);
        ctx.lineTo(consX - 8 * Math.cos(angle - Math.PI / 6), consY - 12 - 8 * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(consX - 8 * Math.cos(angle + Math.PI / 6), consY - 12 - 8 * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();

        // Draw consequence event
        ctx.fillStyle = getCategoryColor(consequence.category);
        ctx.beginPath();
        ctx.arc(consX, consY, 8, 0, Math.PI * 2);
        ctx.fill();

        // Draw significance indicator
        if (consequence.significance >= 7) {
          ctx.strokeStyle = '#f1c40f';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(consX, consY, 12, 0, Math.PI * 2);
          ctx.stroke();
        }
      });
    });

    // Draw title
    ctx.fillStyle = '#ecf0f1';
    ctx.font = 'bold 16px "Sometype Mono", monospace';
    ctx.textAlign = 'center';
    ctx.fillText('Causality Chains', width / 2, 30);

    // Draw subtitle
    ctx.fillStyle = '#7f8c8d';
    ctx.font = '12px "Sometype Mono", monospace';
    const subtitle = selectedChain 
      ? `Showing 1 chain (${chains.length} total)`
      : `Showing ${Math.min(5, chains.length)} chains (${chains.length} total)`;
    ctx.fillText(subtitle, width / 2, 50);
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const padding = { top: 60, bottom: 60, left: 80, right: 80 };
    const graphWidth = rect.width - padding.left - padding.right;

    const allYears = allEvents.map(e => e.year);
    const minYear = Math.min(...allYears);
    const maxYear = Math.max(...allYears);
    const yearRange = maxYear - minYear || 1;

    const displayChains = selectedChain 
      ? chains.filter(c => c.chainId === selectedChain)
      : chains.slice(0, 5);

    // Check if clicking on an event
    displayChains.forEach((chain, chainIndex) => {
      const triggerX = padding.left + ((chain.triggerEvent.year - minYear) / yearRange) * graphWidth;
      const triggerY = padding.top + (chainIndex * 80) + 40;

      // Check trigger event
      if (Math.sqrt((x - triggerX) ** 2 + (y - triggerY) ** 2) <= 14) {
        onEventClick(chain.triggerEvent);
        return;
      }

      // Check consequences
      chain.consequences.forEach((consequence, consIndex) => {
        const consX = padding.left + ((consequence.year - minYear) / yearRange) * graphWidth;
        const consY = triggerY + 60 + (consIndex * 30);

        if (Math.sqrt((x - consX) ** 2 + (y - consY) ** 2) <= 10) {
          onEventClick(consequence);
        }
      });
    });
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const padding = { top: 60, bottom: 60, left: 80, right: 80 };
    const graphWidth = rect.width - padding.left - padding.right;

    const allYears = allEvents.map(e => e.year);
    const minYear = Math.min(...allYears);
    const maxYear = Math.max(...allYears);
    const yearRange = maxYear - minYear || 1;

    const displayChains = selectedChain 
      ? chains.filter(c => c.chainId === selectedChain)
      : chains.slice(0, 5);

    let foundEvent: CausalEvent | null = null;

    displayChains.forEach((chain, chainIndex) => {
      const triggerX = padding.left + ((chain.triggerEvent.year - minYear) / yearRange) * graphWidth;
      const triggerY = padding.top + (chainIndex * 80) + 40;

      if (Math.sqrt((x - triggerX) ** 2 + (y - triggerY) ** 2) <= 14) {
        foundEvent = chain.triggerEvent;
        return;
      }

      chain.consequences.forEach((consequence, consIndex) => {
        const consX = padding.left + ((consequence.year - minYear) / yearRange) * graphWidth;
        const consY = triggerY + 60 + (consIndex * 30);

        if (Math.sqrt((x - consX) ** 2 + (y - consY) ** 2) <= 10) {
          foundEvent = consequence;
        }
      });
    });

    setHoveredEvent(foundEvent);
    setTooltipPosition(foundEvent ? { x: e.clientX, y: e.clientY } : null);
  };

  return (
    <div className="causality-visualizer">
      <div className="causality-header">
        <h3>Causality Chains</h3>
        <p className="causality-subtitle">Explore how events ripple through history</p>
      </div>
      
      <div className="causality-controls">
        <button 
          className={`chain-button ${!selectedChain ? 'active' : ''}`}
          onClick={() => setSelectedChain(null)}
        >
          Show All Chains
        </button>
        {chains.slice(0, 5).map((chain, index) => (
          <button
            key={chain.chainId}
            className={`chain-button ${selectedChain === chain.chainId ? 'active' : ''}`}
            onClick={() => setSelectedChain(chain.chainId)}
          >
            Chain {index + 1}
          </button>
        ))}
      </div>

      <div className="causality-container">
        <canvas
          ref={canvasRef}
          className="causality-canvas"
          onClick={handleCanvasClick}
          onMouseMove={handleCanvasMouseMove}
          onMouseLeave={() => {
            setHoveredEvent(null);
            setTooltipPosition(null);
          }}
        />
        {hoveredEvent && tooltipPosition && (
          <div
            className="causality-tooltip"
            style={{ left: tooltipPosition.x + 10, top: tooltipPosition.y - 10 }}
          >
            <div className="tooltip-year">Year {hoveredEvent.year}</div>
            <div className="tooltip-category">{hoveredEvent.category}</div>
            <div className="tooltip-description">{hoveredEvent.description}</div>
            {hoveredEvent.causedBy && (
              <div className="tooltip-caused">Caused by: {hoveredEvent.causedBy}</div>
            )}
          </div>
        )}
      </div>

      <div className="causality-stats">
        <div className="stat-item">
          <span className="stat-label">Total Chains:</span>
          <span className="stat-value">{chains.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Completed:</span>
          <span className="stat-value">{chains.filter(c => c.completed).length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Avg Probability:</span>
          <span className="stat-value">
            {(chains.reduce((sum, c) => sum + c.probability, 0) / chains.length * 100).toFixed(0)}%
          </span>
        </div>
      </div>
    </div>
  );
};
